<template>
  <section class="px-4 py-10 sm:px-6 lg:px-10">
    <div class="mx-auto max-w-6xl space-y-6">
      <header>
        <div>
          <p class="text-sm uppercase tracking-[0.25em] text-slate-500">Analytics</p>
          <h1 class="text-3xl font-bold text-slate-900">Usage insights</h1>
        </div>
      </header>

      <div class="card-surface p-4">
        <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div class="space-y-1">
            <label class="text-xs font-semibold text-black">Password</label>
            <InputText v-model="password" type="password" placeholder="Password" class="w-full" />
          </div>
          <div class="space-y-1">
            <label class="text-xs font-semibold text-black">Start</label>
            <InputText v-model="startDate" type="date" class="w-full" />
          </div>
          <div class="space-y-1">
            <label class="text-xs font-semibold text-black">End</label>
            <InputText v-model="endDate" type="date" class="w-full" />
          </div>
          <div class="space-y-1">
            <label class="text-xs font-semibold text-black">Region</label>
            <MultiSelect
              v-model="selectedRegions"
              :options="regionOptions"
              option-label="label"
              option-value="value"
              display="chip"
              placeholder="All regions"
              class="w-full"
            />
          </div>
          <div class="space-y-1 xl:col-span-2">
            <label class="text-xs font-semibold text-black">Products</label>
            <MultiSelect
              v-model="selectedProducts"
              :options="productOptions"
              option-label="label"
              option-value="value"
              display="chip"
              filter
              placeholder="All products"
              class="w-full"
            />
          </div>
          <div class="space-y-1">
            <label class="text-xs font-semibold text-black">Languages</label>
            <MultiSelect
              v-model="selectedLanguages"
              :options="languageOptions"
              option-label="label"
              option-value="value"
              display="chip"
              placeholder="All languages"
              class="w-full"
            />
          </div>
          <div class="space-y-1">
            <label class="text-xs font-semibold text-black">Producer ID</label>
            <InputText
              v-model.trim="producerIdQuery"
              placeholder="Search producer ID"
              class="w-full"
              @keydown.enter.prevent="loadData({ resetExpanded: true })"
            />
          </div>
          <div class="flex items-end gap-2">
            <Button :label="loading ? 'Loading...' : 'Load data'" :disabled="loading || !password" class="flex-1" @click="loadData({ resetExpanded: true })" />
            <Button label="Clear" outlined severity="secondary" @click="clearFilters" />
          </div>
        </div>
      </div>

      <div v-if="error" class="rounded-lg bg-rose-50 p-3 text-sm text-rose-800">
        {{ error }}
      </div>

      <div v-if="stats" class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <button
          type="button"
          class="card-surface p-4 text-left transition hover:-translate-y-0.5 hover:shadow-lg"
          @click="selectDetail('submissions')"
        >
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Submissions</p>
          <p class="text-3xl font-bold text-slate-900">{{ stats.submissions }}</p>
        </button>
        <button
          type="button"
          class="card-surface p-4 text-left transition hover:-translate-y-0.5 hover:shadow-lg"
          @click="selectDetail('variants')"
        >
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Variants delivered</p>
          <p class="text-3xl font-bold text-slate-900">{{ stats.total_variants }}</p>
        </button>
        <button
          type="button"
          class="card-surface p-4 text-left transition hover:-translate-y-0.5 hover:shadow-lg disabled:cursor-default disabled:hover:translate-y-0 disabled:hover:shadow"
          :disabled="!stats.top_producers?.length"
          @click="selectProducer(stats.top_producers?.[0]?.label)"
        >
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Top producer</p>
          <p class="text-xl font-semibold text-slate-900">{{ topLabel(stats.top_producers) }}</p>
        </button>
        <div class="card-surface p-4">
          <p class="text-xs font-semibold uppercase tracking-wide text-slate-500">Top product</p>
          <p class="text-xl font-semibold text-slate-900">{{ topLabel(stats.top_products, 'Product') }}</p>
        </div>
      </div>

      <div v-if="stats?.detail_mode" class="card-surface p-4">
        <div class="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 class="text-lg font-semibold text-slate-900">{{ detailTitle }}</h3>
            <p class="text-sm text-slate-600">{{ detailDescription }}</p>
          </div>
          <Button label="Clear details" size="small" outlined severity="secondary" @click="clearDetail" />
        </div>
        <div class="overflow-x-auto">
          <table v-if="stats.detail_mode === 'submissions'" class="min-w-full text-left text-sm">
            <thead>
              <tr class="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <th class="whitespace-nowrap px-3 py-2 font-semibold">Date/time</th>
                <th class="whitespace-nowrap px-3 py-2 font-semibold">Producer ID</th>
                <th class="whitespace-nowrap px-3 py-2 font-semibold">Region</th>
                <th class="whitespace-nowrap px-3 py-2 font-semibold">License region(s)</th>
                <th class="whitespace-nowrap px-3 py-2 font-semibold">Product(s)</th>
                <th class="whitespace-nowrap px-3 py-2 font-semibold">Language(s)</th>
                <th class="whitespace-nowrap px-3 py-2 font-semibold">Variants</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="submission in stats.submission_rows"
                :key="submission.id"
                class="border-b border-slate-100 text-slate-700"
              >
                <td class="whitespace-nowrap px-3 py-2">{{ formatDateTime(submission.submitted_at) }}</td>
                <td class="whitespace-nowrap px-3 py-2 font-semibold text-slate-900">{{ submission.producer_id }}</td>
                <td class="whitespace-nowrap px-3 py-2">{{ formatRegion(submission.region) }}</td>
                <td class="px-3 py-2">{{ formatList(submission.licenses) }}</td>
                <td class="px-3 py-2">{{ formatList(submission.products, 'Product') }}</td>
                <td class="px-3 py-2">{{ formatList(submission.languages, 'Language') }}</td>
                <td class="whitespace-nowrap px-3 py-2">{{ submission.variant_count }}</td>
              </tr>
              <tr v-if="!stats.submission_rows.length">
                <td colspan="7" class="px-3 py-6 text-center text-slate-500">No submissions found in the selected filters.</td>
              </tr>
            </tbody>
          </table>
          <table v-else class="min-w-full text-left text-sm">
            <thead>
              <tr class="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <th class="whitespace-nowrap px-3 py-2 font-semibold">Date/time</th>
                <th class="whitespace-nowrap px-3 py-2 font-semibold">Producer ID</th>
                <th class="whitespace-nowrap px-3 py-2 font-semibold">Variant ID</th>
                <th class="whitespace-nowrap px-3 py-2 font-semibold">Region</th>
                <th class="whitespace-nowrap px-3 py-2 font-semibold">Product context</th>
                <th class="whitespace-nowrap px-3 py-2 font-semibold">Language context</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="variant in stats.variant_rows"
                :key="variant.id"
                class="border-b border-slate-100 text-slate-700"
              >
                <td class="whitespace-nowrap px-3 py-2">{{ formatDateTime(variant.submitted_at) }}</td>
                <td class="whitespace-nowrap px-3 py-2 font-semibold text-slate-900">{{ variant.producer_id }}</td>
                <td class="whitespace-nowrap px-3 py-2">{{ variant.variant_id }}</td>
                <td class="whitespace-nowrap px-3 py-2">{{ formatRegion(variant.region) }}</td>
                <td class="px-3 py-2">{{ formatList(variant.products, 'Product') }}</td>
                <td class="px-3 py-2">{{ formatList(variant.languages, 'Language') }}</td>
              </tr>
              <tr v-if="!stats.variant_rows.length">
                <td colspan="6" class="px-3 py-6 text-center text-slate-500">No variants found in the selected filters.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="stats?.producer_query && !stats?.detail_mode" class="card-surface p-4">
        <div class="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 class="text-lg font-semibold text-slate-900">Producer submissions</h3>
            <p class="text-sm text-slate-600">
              Showing {{ stats.producer_submissions.length }} submission{{ stats.producer_submissions.length === 1 ? '' : 's' }} matching producer ID "{{ stats.producer_query }}".
            </p>
          </div>
          <Button label="Clear producer" size="small" outlined severity="secondary" @click="clearProducer" />
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full text-left text-sm">
            <thead>
              <tr class="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <th class="whitespace-nowrap px-3 py-2 font-semibold">Date/time</th>
                <th class="whitespace-nowrap px-3 py-2 font-semibold">Producer ID</th>
                <th class="whitespace-nowrap px-3 py-2 font-semibold">Region</th>
                <th class="whitespace-nowrap px-3 py-2 font-semibold">License region(s)</th>
                <th class="whitespace-nowrap px-3 py-2 font-semibold">Product(s)</th>
                <th class="whitespace-nowrap px-3 py-2 font-semibold">Language(s)</th>
                <th class="whitespace-nowrap px-3 py-2 font-semibold">Variants</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="submission in stats.producer_submissions"
                :key="submission.id"
                class="border-b border-slate-100 text-slate-700"
              >
                <td class="whitespace-nowrap px-3 py-2">{{ formatDateTime(submission.submitted_at) }}</td>
                <td class="whitespace-nowrap px-3 py-2 font-semibold text-slate-900">{{ submission.producer_id }}</td>
                <td class="whitespace-nowrap px-3 py-2">{{ formatRegion(submission.region) }}</td>
                <td class="px-3 py-2">{{ formatList(submission.licenses) }}</td>
                <td class="px-3 py-2">{{ formatList(submission.products, 'Product') }}</td>
                <td class="px-3 py-2">{{ formatList(submission.languages, 'Language') }}</td>
                <td class="whitespace-nowrap px-3 py-2">{{ submission.variant_count }}</td>
              </tr>
              <tr v-if="!stats.producer_submissions.length">
                <td colspan="7" class="px-3 py-6 text-center text-slate-500">No submissions found for this producer ID in the selected filters.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="stats" class="grid gap-6 xl:grid-cols-2">
        <div class="space-y-6">
          <div class="card-surface p-4">
            <h3 class="text-lg font-semibold text-slate-900">Top 5 producer IDs</h3>
            <div class="producer-chart" @click="handleProducerChartClick">
              <apexchart type="bar" :height="producerChartHeight" :options="barOptions('Producer ID')" :series="seriesFor(stats.top_producers)" />
            </div>
            <Button
              v-if="stats.has_more_producers"
              :label="loadingAllProducers ? 'Loading all...' : 'Load all producer IDs'"
              size="small"
              class="mt-3 !bg-[#007C89] !border-[#007C89] !text-white"
              :disabled="loading"
              @click="loadAllProducers"
            />
          </div>
          <div class="card-surface p-4">
            <h3 class="text-lg font-semibold text-slate-900">Top license regions</h3>
            <apexchart type="bar" :height="licenseChartHeight" :options="barOptions('License region')" :series="seriesFor(stats.top_licenses)" />
            <Button
              v-if="stats.has_more_licenses"
              :label="loadingAllLicenses ? 'Loading all...' : 'Load all license regions'"
              size="small"
              class="mt-3 !bg-[#007C89] !border-[#007C89] !text-white"
              :disabled="loading"
              @click="loadAllLicenses"
            />
          </div>
          <div class="card-surface p-4">
            <h3 class="text-lg font-semibold text-slate-900">Language breakdown</h3>
            <apexchart type="donut" height="320" :options="donutOptions" :series="languageSeries" />
          </div>
        </div>
        <div class="card-surface p-4">
          <h3 class="text-lg font-semibold text-slate-900">Product breakdown</h3>
          <apexchart type="bar" height="860" :options="barOptions('Product')" :series="seriesFor(stats.top_products)" />
        </div>
      </div>

      <div v-if="stats" class="card-surface p-4">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 class="text-lg font-semibold text-slate-900">Analytics exports</h3>
            <p class="text-sm text-slate-600">Download summary tabs or detailed submissions matching the current filters.</p>
          </div>
          <div class="flex flex-col gap-2 sm:flex-row">
            <Button
              :label="exportingSummary ? 'Preparing XLSX...' : 'Summary XLSX'"
              class="!bg-[#007C89] !border-[#007C89] !text-white"
              :disabled="exportingSummary || exporting || !password"
              @click="downloadSummaryXlsx"
            />
            <Button
              :label="exporting ? 'Preparing CSV...' : 'Download detail CSV'"
              outlined
              severity="secondary"
              :disabled="exporting || exportingSummary || !password"
              @click="downloadCsv"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import { PRODUCTS_CA, PRODUCTS_US } from '@/front/constants/catalog';
import { exportAnalyticsCsv, exportAnalyticsSummaryXlsx, fetchAnalyticsSummary } from '@/front/services/analytics';

const password = ref('');
const startDate = ref('');
const endDate = ref('');
const selectedRegions = ref([]);
const selectedProducts = ref([]);
const selectedLanguages = ref([]);
const producerIdQuery = ref('');
const detailMode = ref('');
const loading = ref(false);
const exporting = ref(false);
const exportingSummary = ref(false);
const loadingAllProducers = ref(false);
const loadingAllLicenses = ref(false);
const error = ref('');
const stats = ref(null);
const allProducersLoaded = ref(false);
const allLicensesLoaded = ref(false);
const passwordStorageKey = 'forestersAnalyticsPassword';

const regionOptions = [
  { label: 'United States', value: 'us' },
  { label: 'Canada', value: 'ca' }
];
const productOptions = [
  ...PRODUCTS_US.map((product) => ({ label: `US: ${product.label}`, value: product.key })),
  ...PRODUCTS_CA.map((product) => ({ label: `Canada: ${product.label}`, value: product.key }))
];
const productLabelMap = Object.fromEntries(
  [...PRODUCTS_US, ...PRODUCTS_CA].map((product) => [
    product.key,
    product.label.replace(/\s*\([^)]*\)\s*$/, '')
  ])
);
const languageOptions = [
  { label: 'English', value: 'en' },
  { label: 'Spanish', value: 'es' },
  { label: 'French', value: 'fr' }
];
const languageLabelMap = Object.fromEntries(languageOptions.map((option) => [option.value, option.label]));

const setDefaultRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  startDate.value = start.toISOString().slice(0, 10);
  endDate.value = end.toISOString().slice(0, 10);
};

const displayLabel = (value, chartLabel = '') => {
  if (chartLabel.includes('Product')) return productLabelMap[value] || value;
  if (chartLabel.includes('Language')) return languageLabelMap[value] || value;
  return value;
};

const labelsForChart = (label) =>
  (stats.value?.[keyFromLabel(label)] || []).map((item) => displayLabel(item.label, label));

const barOptions = (label) => ({
  chart: {
    events: {
      dataPointSelection: (_event, _chartContext, config) => {
        if (!label.includes('Producer')) return;
        selectProducer(stats.value?.top_producers?.[config.dataPointIndex]?.label);
      }
    },
    toolbar: {
      show: true,
      tools: { download: true, selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false },
      export: {
        csv: {
          filename: `${label.toLowerCase().replace(/\\s+/g, '-')}-data`
        },
        png: {
          filename: `${label.toLowerCase().replace(/\\s+/g, '-')}-chart`
        },
        svg: {
          filename: `${label.toLowerCase().replace(/\\s+/g, '-')}-chart`
        }
      }
    }
  },
  plotOptions: {
    bar: { horizontal: true, borderRadius: 4, columnWidth: '55%', distributed: true }
  },
  colors: ['#007C89', '#0099a3', '#00b3bd', '#00ced5', '#00e8ed'],
  dataLabels: { enabled: true },
  xaxis: { categories: labelsForChart(label), labels: { style: { fontSize: '12px' } } },
  yaxis: { labels: { style: { fontSize: '12px' } } },
  tooltip: { y: { formatter: (val) => `${val}` } }
});

const keyFromLabel = (label) => {
  if (label.includes('Producer')) return 'top_producers';
  if (label.includes('License')) return 'top_licenses';
  if (label.includes('Product')) return 'top_products';
  return '';
};

const seriesFor = (items = []) => [{ name: 'Count', data: items.map((item) => item.count) }];

const donutOptions = computed(() => ({
  labels: (stats.value?.languages || []).map((item) => item.label),
  colors: ['#007C89', '#00a3b2', '#00bccb', '#00d6e2', '#00eff9'],
  legend: { position: 'bottom' },
  chart: {
    toolbar: {
      show: true,
      tools: { download: true, selection: false, zoom: false, zoomin: false, zoomout: false, pan: false, reset: false },
      export: {
        csv: {
          filename: 'language-breakdown'
        },
        png: {
          filename: 'language-breakdown'
        },
        svg: {
          filename: 'language-breakdown'
        }
      }
    }
  }
}));

const languageSeries = computed(() => (stats.value?.languages || []).map((item) => item.count));
const chartHeightFor = (items = []) => Math.max(260, items.length * 42 + 80);
const producerChartHeight = computed(() => chartHeightFor(stats.value?.top_producers));
const licenseChartHeight = computed(() => chartHeightFor(stats.value?.top_licenses));

const topLabel = (items, label = '') =>
  items && items.length ? `${displayLabel(items[0].label, label)} (${items[0].count})` : '-';
const detailTitle = computed(() =>
  stats.value?.detail_mode === 'variants' ? 'Variant details' : 'Submission details'
);
const detailDescription = computed(() => {
  if (stats.value?.detail_mode === 'variants') {
    const count = stats.value?.variant_rows?.length || 0;
    return `Showing ${count} variant${count === 1 ? '' : 's'} in the selected filters.`;
  }

  const count = stats.value?.submission_rows?.length || 0;
  return `Showing ${count} submission${count === 1 ? '' : 's'} in the selected filters.`;
});

const clearFilters = () => {
  selectedRegions.value = [];
  selectedProducts.value = [];
  selectedLanguages.value = [];
  producerIdQuery.value = '';
  detailMode.value = '';
  allProducersLoaded.value = false;
  allLicensesLoaded.value = false;
};

const clearDetail = async () => {
  detailMode.value = '';
  await loadData({ allProducers: allProducersLoaded.value, allLicenses: allLicensesLoaded.value });
};

const selectDetail = async (mode) => {
  detailMode.value = mode;
  await loadData({ allProducers: allProducersLoaded.value, allLicenses: allLicensesLoaded.value });
};

const clearProducer = async () => {
  producerIdQuery.value = '';
  await loadData({ allProducers: allProducersLoaded.value, allLicenses: allLicensesLoaded.value });
};

const selectProducer = async (producerId) => {
  if (!producerId) return;
  producerIdQuery.value = producerId;
  detailMode.value = '';
  await loadData({ allProducers: allProducersLoaded.value, allLicenses: allLicensesLoaded.value });
};

const handleProducerChartClick = (event) => {
  const label = event.target?.textContent?.trim();
  const producer = stats.value?.top_producers?.find((item) => item.label === label);
  if (producer) selectProducer(producer.label);
};

const formatDateTime = (value) => {
  if (!value) return '-';
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short'
  }).format(new Date(value));
};

const formatRegion = (value) => (value === 'ca' ? 'Canada' : value === 'us' ? 'United States' : value || '-');
const formatList = (items = [], label = '') =>
  items.length ? items.map((item) => displayLabel(item, label)).join(', ') : '-';

const loadData = async ({ allProducers = allProducersLoaded.value, allLicenses = allLicensesLoaded.value, resetExpanded = false } = {}) => {
  error.value = '';
  if (!password.value) {
    error.value = 'Password required.';
    return;
  }
  if (resetExpanded) {
    allProducers = false;
    allLicenses = false;
  }
  loading.value = true;
  try {
    const response = await fetchAnalyticsSummary({
      password: password.value,
      startDate: startDate.value,
      endDate: endDate.value,
      regions: selectedRegions.value,
      products: selectedProducts.value,
      languages: selectedLanguages.value,
      producerId: producerIdQuery.value,
      detail: detailMode.value,
      allProducers,
      allLicenses
    });
    stats.value = response.data.data;
    allProducersLoaded.value = allProducers;
    allLicensesLoaded.value = allLicenses;
  } catch (err) {
    if (err.response?.status === 401) {
      error.value = 'Unauthorized. Check password.';
    } else {
      error.value = 'Unable to load analytics.';
    }
    stats.value = null;
  } finally {
    loading.value = false;
  }
};

const loadAllProducers = async () => {
  loadingAllProducers.value = true;
  try {
    await loadData({ allProducers: true, allLicenses: allLicensesLoaded.value });
  } finally {
    loadingAllProducers.value = false;
  }
};

const loadAllLicenses = async () => {
  loadingAllLicenses.value = true;
  try {
    await loadData({ allProducers: allProducersLoaded.value, allLicenses: true });
  } finally {
    loadingAllLicenses.value = false;
  }
};

const filenameFromDisposition = (disposition) => {
  const match = disposition?.match(/filename="?([^"]+)"?/i);
  return match?.[1] || `foresters-analytics-${new Date().toISOString().slice(0, 10)}.csv`;
};

const downloadCsv = async () => {
  error.value = '';
  if (!password.value) {
    error.value = 'Password required.';
    return;
  }

  exporting.value = true;
  try {
    const response = await exportAnalyticsCsv({
      password: password.value,
      startDate: startDate.value,
      endDate: endDate.value,
      regions: selectedRegions.value,
      products: selectedProducts.value,
      languages: selectedLanguages.value,
      producerId: producerIdQuery.value
    });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filenameFromDisposition(response.headers['content-disposition']);
    link.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    if (err.response?.status === 401) {
      error.value = 'Unauthorized. Check password.';
    } else {
      error.value = 'Unable to export analytics.';
    }
  } finally {
    exporting.value = false;
  }
};

const downloadSummaryXlsx = async () => {
  error.value = '';
  if (!password.value) {
    error.value = 'Password required.';
    return;
  }

  exportingSummary.value = true;
  try {
    const response = await exportAnalyticsSummaryXlsx({
      password: password.value,
      startDate: startDate.value,
      endDate: endDate.value,
      regions: selectedRegions.value,
      products: selectedProducts.value,
      languages: selectedLanguages.value,
      producerId: producerIdQuery.value
    });
    const url = URL.createObjectURL(response.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filenameFromDisposition(response.headers['content-disposition']);
    link.click();
    URL.revokeObjectURL(url);
  } catch (err) {
    if (err.response?.status === 401) {
      error.value = 'Unauthorized. Check password.';
    } else {
      error.value = 'Unable to export analytics summary.';
    }
  } finally {
    exportingSummary.value = false;
  }
};

onMounted(() => {
  setDefaultRange();
  password.value = localStorage.getItem(passwordStorageKey) || '';
});

watch(password, (value) => {
  if (value) {
    localStorage.setItem(passwordStorageKey, value);
  } else {
    localStorage.removeItem(passwordStorageKey);
  }
});
</script>

<style scoped>
.producer-chart :deep(.apexcharts-yaxis-label) {
  cursor: pointer;
}
</style>
